import { useCallback, useEffect, useState } from 'react';
import { gamificacionService } from '../services/gamificacionService';
import { ejercicioService } from '../services/ejercicioService';
import { perfilService } from '../services/perfilService';
import type { ProgresoEjercicio } from '../types/ejercicio';
import type { Resultado } from '../types/cuestionario';
import {
  clasificarDashboard,
  diasDeLaSemana,
  historialPorDia,
  type ActividadHistorial,
  type DiaSemana,
  type EstadisticasUsuario,
  type EstadoDashboard,
} from '../types/dashboard';

interface DashboardData {
  estado: EstadoDashboard;
  stats: EstadisticasUsuario | null;
  dias: DiaSemana[];
  /** P7. Vacio mientras carga o si ninguna fuente respondio. */
  historial: ActividadHistorial[];
  reintentar: () => void;
}

/**
 * Carga los datos del dashboard y los clasifica en la maquina de estados de
 * `types/dashboard.ts`.
 *
 * @param activo `false` cuando la pagina va a redirigir (psicologo). Evita la
 * peticion inútil: sin esto, el efecto se dispara en el montaje y se pide
 * `/gamificacion/estadisticas` aunque el componente devuelva `<Navigate>`.
 *
 * Dos peticiones en paralelo: las estadisticas (contadores) y el progreso de
 * ejercicios (fechas), que es lo que alimenta el anillo de la semana. Si el
 * progreso falla se degrada a una semana vacia en vez de tumbar la pantalla:
 * el contador es mas importante que el anillo.
 */
export function useDashboardData(activo = true): DashboardData {
  const [stats, setStats] = useState<EstadisticasUsuario | null>(null);
  const [progreso, setProgreso] = useState<ProgresoEjercicio[]>([]);
  const [historialFuentes, setHistorialFuentes] = useState<{
    ejercicios: ProgresoEjercicio[];
    evaluaciones: Resultado[];
  }>({ ejercicios: [], evaluaciones: [] });
  const [error, setError] = useState<unknown>(null);
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const [intento, setIntento] = useState(0);

  // Sin esto, perder la red no cambia nada en pantalla hasta que el usuario
  // navega o recarga.
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!activo) return;
    let vivo = true;
    setError(null);

    Promise.all([
      gamificacionService.getEstadisticas(),
      ejercicioService.getProgreso().catch(() => [] as ProgresoEjercicio[]),
    ])
      .then(([statsData, progresoData]) => {
        if (!vivo) return;
        setStats(statsData);
        setProgreso(progresoData);
      })
      .catch((err) => {
        if (!vivo) return;
        setError(err);
      });

    return () => {
      vivo = false;
    };
  }, [activo, intento]);

  const reintentar = useCallback(() => {
    setStats(null);
    setError(null);
    setIntento((n) => n + 1);
  }, []);

  // Las dos peticiones del historial van APARTADAS y todas con `.catch` a lista
  // vacia (P7).
  //
  // No se encadenan al `Promise.all` de las estadisticas a proposito: si una
  // fuente falla, el historial entero se pierde y con ella la pantalla. Cada
  // `catch` devuelve `[]`, que es el estado "no hay nada que mostrar", no un
  // fallo. Un historial parcial es mejor que ningun historial, y esta seccion
  // es opcional por definicion (es opt-in).
  useEffect(() => {
    if (!activo) return;
    let vivo = true;

    Promise.all([
      ejercicioService.getProgreso().catch(() => [] as ProgresoEjercicio[]),
      perfilService.getHistorialEvaluaciones().catch(() => [] as Resultado[]),
    ]).then(([ejercicios, evaluaciones]) => {
      if (!vivo) return;
      setHistorialFuentes({ ejercicios, evaluaciones });
    });

    return () => {
      vivo = false;
    };
  }, [activo, intento]);

  return {
    estado: !activo ? 'redirigiendo' : clasificarDashboard(stats, online, error),
    stats,
    dias: diasDeLaSemana(progreso),
    historial: historialPorDia(historialFuentes),
    reintentar,
  };
}
